import { roundNumber } from 'common/helpers/CommonUtil';

/* =================================================== Exported Constants =================================================== */
export const ONE_SECOND_IN_MS = 1000;
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;
export const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;
export const ONE_DAY_IN_MS = 24 * ONE_HOUR_IN_MS;
export const START_OF_CALENDAR = new Date(1970, 0, 1, 0, 0, 0, 0);

/* =================================================== Local Functions =================================================== */
const getFormattedNumber = (number: number | string, length: number = 2) => {
  let numberInString = String(number);

  if (numberInString.length < length) {
    numberInString = `0${numberInString}`;
    numberInString = getFormattedNumber(numberInString, length);
  }

  return numberInString;
};

/* =================================================== Exported Functions =================================================== */
export const formatDateIntlCustom: TFormatDateIntlCustom = (timestamp, locale = 'da-DK') => {
  const date = new Date(timestamp);

  // Formatter for the date part
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Copenhagen'
  });

  // Formatter for the time part
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Copenhagen'
  });

  const dateParts = dateFormatter.formatToParts(date).reduce(
    (acc, part) => {
      acc[part.type] = part.value;
      return acc;
    },
    {} as Record<string, string>
  );

  const timeParts = timeFormatter.formatToParts(date).reduce(
    (acc, part) => {
      acc[part.type] = part.value;
      return acc;
    },
    {} as Record<string, string>
  );

  // Calculate the GMT offset
  const timeZoneOffset = -date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(timeZoneOffset) / 60);
  const offsetSign = timeZoneOffset >= 0 ? '+' : '-';
  const formattedOffset = `GMT ${offsetSign}${String(offsetHours).padStart(2, '0')}`;

  return `${dateParts.day}.${dateParts.month}.${dateParts.year}, ${timeParts.hour}:${timeParts.minute} ${formattedOffset}`;
};

export const getDateOnly: TGetDateOnly = (date, { separator = '-' } = {}) => {
  let day, month, year;
  const formattedDate = new Date(date);

  day = getFormattedNumber(formattedDate.getDate());
  month = getFormattedNumber(formattedDate.getMonth() + 1);
  year = formattedDate.getFullYear();

  const dateInString = `${year}${separator}${month}${separator}${day}`;

  return dateInString;
};

export const getTimeOnly: TGetTimeOnly = (date, { gapInMinutes } = {}) => {
  let hours = date.getHours();
  let minutes = date.getMinutes();

  if ((gapInMinutes ?? 0) > 0) {
    minutes = roundNumber(minutes, gapInMinutes);

    if (minutes >= 60) {
      hours = hours + 1;
      minutes = minutes - 60;

      if (hours >= 24) {
        hours = 0;
      }
    }
  }

  const formattedHours = getFormattedNumber(hours);
  const formattedMinutes = getFormattedNumber(minutes);

  return `${formattedHours}:${formattedMinutes}`;
};

export const getTimesOfDay: TGetTimesOfDay = (interval = 5) => {
  const times = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const formattedHours = getFormattedNumber(hour);
      const formattedMinutes = getFormattedNumber(minute);
      times.push(`${formattedHours}:${formattedMinutes}`);
    }
  }

  return times;
};

/* =================================================== Local Types =================================================== */
type TFormatDateIntlCustom = (timestamp: number, locale?: string) => string;

type TGetDateOnly = (date: Date | string, options?: { separator?: string }) => string;

type TGetTimeOnly = (date: Date, options?: { gapInMinutes?: number }) => string;

type TGetTimesOfDay = (interval?: number) => string[];
